/** Hash and upload files straight to storage through signed URLs (no API size limit). */
import { api } from "../api/client";
import type { SignedUpload } from "../api/types";

export async function sha256(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export interface UploadedFile {
  key: string;
  sha256: string;
  filename: string;
  upload_id?: string | null;
}

export async function uploadFile(
  endpoint: string,
  file: File,
  kind: "guarantee" | "asset" | "evidence" | "branding",
  extra: Record<string, unknown> = {},
  onProgress?: (message: string) => void,
): Promise<UploadedFile> {
  onProgress?.(`Checking ${file.name}…`);
  const digest = await sha256(file);
  const contentType =
    kind === "guarantee" ? "application/pdf" : file.type || "application/octet-stream";
  const target = await api.post<SignedUpload>(endpoint, {
    kind,
    filename: file.name,
    size: file.size,
    sha256: digest,
    content_type: contentType,
    ...extra,
  });
  if (!target.already_uploaded && target.url) {
    onProgress?.(`Uploading ${file.name}…`);
    const response = await fetch(target.url, { method: "PUT", headers: target.headers, body: file });
    if (!response.ok && response.status !== 412) {
      throw new Error(`Upload of ${file.name} failed (${response.status}).`);
    }
  }
  return { key: target.key, sha256: digest, filename: file.name, upload_id: target.upload_id };
}
